/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import { updateAuthConfig, getAuthConfig, updateAuthInfo, updateHeaders, fetch, type Config } from '../fetch';
import RouteParser from '../../router/route-parser';

interface TokenInfo {
  expires_in: number;
  eA?: number;
  session_index?: string;
  csrf?: string;
  token_type?: string;
  access_token?: string;
}

interface PegaAuthConfig {
  session_index?: string;
  clientId?: string;
  client_id?: string;
  clientSecret?: string;
  client_secret?: string;
  redirectUri?: string;
  redirect_uri?: string;
  authorizeUri?: string;
  tokenUri?: string;
  endPoints?: {
    authorize: string;
    token: string;
  };
  authService?: string;
  authentication_service?: string;
  appAlias?: string;
  userIdentifier?: string;
  user_identifier?: string;
  password?: string;
  sessionIndex?: string;
  tokenInfo?: {
    session_index: string;
  };
  codeVerifier?: string;
  secureCookie?: boolean;
  onTokenRetrieval?: (tokenInfo: TokenInfo) => void;
}

class PegaAuth {
  config: PegaAuthConfig;
  ssKeyConfig: any;

  constructor(config: PegaAuthConfig, useSessionStorage = true) {
    this.config = config;
    if (useSessionStorage) {
      this.ssKeyConfig = config;
      this.reloadConfig();
    } else {
      this.updateConfig(config, false);
    }
  }

  static init(config: PegaAuthConfig, useSessionStorage = true) {
    return new PegaAuth(config, useSessionStorage);
  }

  updateConfig(config: PegaAuthConfig, useSessionStorage = true) {
    if (useSessionStorage) {
      window.sessionStorage.setItem(this.ssKeyConfig, JSON.stringify(this.config));
    } else {
      updateAuthConfig(config);
    }
  }

  reloadConfig(useSessionStorage = true) {
    if (useSessionStorage) {
      const peConfig = window.sessionStorage.getItem(this.ssKeyConfig);
      this.config = peConfig ? JSON.parse(peConfig) : null;
    } else {
      this.config = getAuthConfig();
    }
  }

  // For PKCE the authorize includes a code_challenge & code_challenge_method as well

  async buildAuthorizeUrl(state?: any) {
    state = !state ? window.btoa(window.location.origin) : state;
    let { sessionIndex } = this.config;
    const { password } = this.config;
    const clientId = !this.config.clientId ? this.config.client_id : this.config.clientId;
    const redirectUri = !this.config.redirectUri ? this.config.redirect_uri : this.config.redirectUri;
    const authorizeUri = !this.config.authorizeUri ? this.config.endPoints?.authorize : this.config.authorizeUri;
    const authService = !this.config.authService
      ? this.config.authentication_service || 'pega'
      : this.config.authService;
    const appAlias = !this.config.appAlias ? RouteParser.getApplicationAlias() : this.config.appAlias;
    const userIdentifier = !this.config.userIdentifier ? this.config.user_identifier : this.config.userIdentifier;

    sessionIndex = !sessionIndex ? this.config.tokenInfo?.session_index : sessionIndex;
    // Add explicit credentials if specified to try to avoid login popup

    let moreAuthArgs = '';
    if (sessionIndex) {
      moreAuthArgs = `&session_index=${sessionIndex}`;
    } else if (`${userIdentifier}`) {
      moreAuthArgs = `&UserIdentifier=${userIdentifier}`;
    } else if (password) {
      moreAuthArgs = `&Password=${window.atob(password)}`;
    }

    // Generate random string of 64 chars for verifier.  RFC 7636 says from 43-128 chars
    const buf = new Uint8Array(64);
    window.crypto?.getRandomValues(buf);
    this.config.codeVerifier = this.base64UrlSafeEncode(buf);
    // Persist codeVerifier in session storage so it survives the redirects that are to follow
    this.updateConfig(this.config, false);

    // Trim alias to include just the real alias piece
    const additionalScope = appAlias ? `+app.alias.${appAlias.replace(/^app\//, '')}` : '';
    return this.getCodeChallenge(this.config.codeVerifier)
      ?.then((cc) => {
        // Now includes new enable_psyncId=true and session_index params
        return `${authorizeUri}?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=openid${additionalScope}&state=${state}&code_challenge=${cc}&code_challenge_method=S256&authentication_service=${authService}&enable_psyncId=true${moreAuthArgs}`;
      })
      ?.catch(() => null);
  }

  /* Retrieve token(s) and close login window */
  getTokenAndFinish = (code: any) => {
    window.removeEventListener('message', this.authMessageHandler, false);
    this.getToken(code, false)
      .then(() => {
        // console.log(token);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  /* Handler to receive the auth code */
  authMessageHandler = (event: MessageEvent) => {
    // Check origin to make sure it is the redirect origin
    if (event.origin !== this.getRedirectUriOrigin()) return;
    if (!event.data || !event.data.type || event.data.type !== 'PegaAuth') return;
    const code = event.data.code.toString();
    this.getTokenAndFinish(code);
  };

  getRedirectUriOrigin = () => {
    let { redirectUri } = this.config;
    redirectUri = redirectUri || this.config.redirect_uri || '';
    const nRootOffset = redirectUri.indexOf('//');
    const nFirstPathOffset = redirectUri.indexOf('/', nRootOffset + 2);
    return nFirstPathOffset !== -1 ? redirectUri.substring(0, nFirstPathOffset) : redirectUri;
  };

  // For PKCE token endpoint includes code_verifier
  // eslint-disable-next-line sonarjs/cognitive-complexity
  getToken(authCode: string, useSessionStorage: boolean) {
    // Reload config to pick up the previously stored codeVerifier
    this.reloadConfig(useSessionStorage);
    // this.config = config;
    const clientId = !this.config.clientId ? this.config.client_id : this.config.clientId;
    const clientSecret = !this.config.clientSecret ? this.config.client_secret : this.config.clientSecret;
    const redirectUri = !this.config.redirectUri ? this.config.redirect_uri : this.config.redirectUri;
    const tokenUri = !this.config.tokenUri ? this.config.endPoints?.token : this.config.tokenUri;

    const { codeVerifier } = this.config;
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const code: any = authCode || urlParams.get('code');

    const formData = new URLSearchParams();
    if (clientId) {
      formData.append('client_id', clientId);
    }
    if (clientSecret) {
      formData.append('client_secret', clientSecret);
    }
    formData.append('grant_type', 'authorization_code');
    formData.append('code', code);
    if (redirectUri) {
      formData.append('redirect_uri', redirectUri);
    }
    if (codeVerifier) {
      formData.append('code_verifier', codeVerifier);
    }
    if (this.config?.secureCookie) {
      formData.append('send_token_as_cookie', 'true');
    }

    return fetch(tokenUri, {
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/x-www-form-urlencoded'
      }) as unknown as Config['headers'],

      body: formData.toString()
    })
      .then((response: any) => response.data)
      .then((tokenInfo: TokenInfo) => {
        // .expires_in contains the # of seconds before access token expires
        // add property to keep track of current time when the token expires

        tokenInfo.eA = Date.now() + tokenInfo.expires_in * 1000;
        let bUpdateConfig = false;
        if (this.config.codeVerifier) {
          delete this.config.codeVerifier;
          bUpdateConfig = true;
        }
        // If there is a session_index then move this to the peConfig structure (as used on authorize)
        if (tokenInfo.session_index) {
          this.config.session_index = tokenInfo.session_index;
          // Also update the session storage to contain this update
          bUpdateConfig = true;
        }
        if (bUpdateConfig) {
          this.updateConfig(this.config, useSessionStorage);
          if (this.config?.secureCookie) {
            updateHeaders({
              pzCTkn: tokenInfo?.csrf
            });
          } else {
            updateAuthInfo({
              tokenInfo
            });
            updateHeaders({
              Authorization: `${tokenInfo?.token_type} ${tokenInfo?.access_token}`
            });
          }
          const { onTokenRetrieval } = this.config;
          // call the callback onTokenRetrieval to update pega-embed with new token info
          if (onTokenRetrieval && typeof onTokenRetrieval === 'function') {
            onTokenRetrieval(tokenInfo);
          }
          window.postMessage('oAuthSuccess');
        }
        return tokenInfo;
      })
      .catch(() => null);
  }

  sha256Hash(str: string) {
    return window.crypto?.subtle?.digest('SHA-256', new TextEncoder().encode(str));
  }

  // Base64 encode

  encode64(buff: ArrayBuffer) {
    return window.btoa(new Uint8Array(buff).reduce((s, b) => s + String.fromCharCode(b), ''));
  }

  /*
   * Base64 url safe encoding of an array
   */
  base64UrlSafeEncode(buf: ArrayBuffer) {
    return this.encode64(buf).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /* Calc code verifier if necessary
   */

  getCodeChallenge(code_verifier: string) {
    return this.sha256Hash(code_verifier)
      ?.then((hashed) => {
        return this.base64UrlSafeEncode(hashed);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        return null;
      });
  }
}

export default PegaAuth;
